import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Bell, CheckCheck, X, RefreshCw, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_CONFIG = {
  high: { icon: AlertTriangle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "HIGH" },
  medium: { icon: AlertCircle, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "MEDIUM" },
  low: { icon: Info, color: "text-zinc-700", bg: "bg-zinc-50", border: "border-zinc-200", label: "LOW" },
};

const CATEGORY_LABEL = {
  ad_limit: "Active ad limit",
  page_limit: "Page rate limit",
  rate_limit: "Rate limit",
  permission: "Permission denied",
  token_invalid: "Token invalid",
  other: "Other",
};

function fmtRelative(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [filter, setFilter] = useState("all"); // all | high | medium | low

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { include_dismissed: showDismissed } });
      setItems(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch (e) {
      toast.error("Failed to load notifications");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showDismissed]);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch { toast.error("Failed"); }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("All marked as read");
      load();
    } catch { toast.error("Failed"); }
  };

  const dismiss = async (id) => {
    try {
      await api.post("/notifications/dismiss", { ids: [id] });
      load();
    } catch { toast.error("Failed"); }
  };

  const dismissAll = async () => {
    try {
      await api.post("/notifications/dismiss", { all: true });
      toast.success("Cleared notifications");
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = items.filter((n) => filter === "all" || n.severity === filter);
  const counts = {
    all: items.length,
    high: items.filter((n) => n.severity === "high").length,
    medium: items.filter((n) => n.severity === "medium").length,
    low: items.filter((n) => n.severity === "low").length,
  };

  return (
    <div data-testid="notifications-page" className="fade-in">
      <PageHeader
        eyebrow="System Alerts"
        title="Notifications"
        description="Meta and Google Ads API issues, rate-limit hits, and permission errors. Capacity problems (e.g. Meta's active-ad limit) show up here with suggested fixes."
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={markAllRead} variant="outline" data-testid="mark-all-read-btn"
              className="rounded-none font-mono uppercase tracking-wider text-xs">
              <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
            </Button>
            <Button onClick={dismissAll} variant="outline" data-testid="dismiss-all-btn"
              className="rounded-none font-mono uppercase tracking-wider text-xs text-red-700 border-red-300 hover:bg-red-50">
              <X className="w-4 h-4 mr-2" /> Clear all
            </Button>
            <Button onClick={load} disabled={loading}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs"
              data-testid="notif-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {/* Filter tabs */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="inline-flex border border-zinc-300 bg-white" data-testid="notif-tabs">
            {[
              { v: "all", label: "All" },
              { v: "high", label: "High" },
              { v: "medium", label: "Medium" },
              { v: "low", label: "Low" },
            ].map((t, i) => (
              <button
                key={t.v}
                data-testid={`notif-tab-${t.v}`}
                onClick={() => setFilter(t.v)}
                className={`px-4 h-9 text-xs font-mono uppercase tracking-wider ${i > 0 ? "border-l border-zinc-300" : ""} ${
                  filter === t.v ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"
                }`}>
                {t.label}
                <span className="ml-2 opacity-70">{counts[t.v]}</span>
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs font-mono text-zinc-600 cursor-pointer">
            <input type="checkbox" data-testid="show-dismissed-toggle" checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)} className="accent-zinc-900" />
            Show dismissed
          </label>
        </div>

        {/* List */}
        <div className="bg-white border border-zinc-200">
          {filtered.length === 0 && !loading && (
            <div className="p-12 text-center" data-testid="notif-empty">
              <Bell className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
              <div className="font-display font-semibold uppercase tracking-tight">All clear</div>
              <div className="text-xs text-zinc-500 font-mono mt-1">
                No {filter === "all" ? "" : filter} notifications. Issues from Meta or Google Ads will appear here automatically.
              </div>
            </div>
          )}
          <ul className="divide-y divide-zinc-100">
            {filtered.map((n) => {
              const cfg = SEVERITY_CONFIG[n.severity] || SEVERITY_CONFIG.low;
              const Icon = cfg.icon;
              return (
                <li key={n.id} data-testid={`notif-${n.id}`}
                    className={`p-5 ${n.read ? "opacity-70" : ""} ${n.dismissed ? "opacity-40" : ""}`}>
                  <div className="flex items-start gap-4">
                    <div className={`p-2 ${cfg.bg} ${cfg.border} border`}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-600 px-1.5 py-0.5">
                          {CATEGORY_LABEL[n.category] || n.category}
                        </span>
                        {n.count > 1 && (
                          <span className="text-[10px] font-mono text-zinc-500">×{n.count}</span>
                        )}
                        <span className="text-[10px] font-mono text-zinc-400 ml-auto">{fmtRelative(n.last_seen_at)}</span>
                      </div>
                      <div className="mt-1 font-medium text-zinc-900">{n.title}</div>
                      <div className="mt-1 text-xs text-zinc-600">{n.hint}</div>
                      {n.last_error && (
                        <details className="mt-2">
                          <summary className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-900">
                            Raw error
                          </summary>
                          <pre className="mt-1 text-[11px] font-mono bg-zinc-50 border border-zinc-200 p-2 whitespace-pre-wrap break-all max-h-40 overflow-auto">
                            {n.last_error}
                          </pre>
                        </details>
                      )}
                      <div className="mt-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
                        {!n.read && !n.dismissed && (
                          <button onClick={() => markRead(n.id)} data-testid={`mark-read-${n.id}`}
                            className="hover:underline text-zinc-700">
                            Mark read
                          </button>
                        )}
                        {!n.dismissed && (
                          <button onClick={() => dismiss(n.id)} data-testid={`dismiss-${n.id}`}
                            className="hover:underline text-red-700">
                            Dismiss
                          </button>
                        )}
                        {n.account_id && (
                          <span className="text-zinc-400">acct {n.account_id}</span>
                        )}
                        {n.operation && (
                          <span className="text-zinc-400">{n.operation}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
